/**
 * Properties — the same list engine as Contacts, pointed at a different area.
 * The only screen-specific parts are the metrics, the primary cell and where a
 * row click goes.
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { ListScreen } from '../components/list-screen.js'
import { BulkRecordPicker, type BulkAction } from '../components/bulk-menu.js'
import { RowTray } from '../components/row-tray.js'
import { propertyMark } from '../components/landing-marks.js'
import { humanValue } from '../components/filter-value.js'
import { lastActivity, text } from '../components/card-line.js'
import type { CardLine } from '../components/data-list.js'
import { useList, type ListRow, type UseListResult } from '../lib/use-list.js'
import { openItems, copyItem } from '../lib/row-menu.js'
import { IconCompany, IconPin } from '../frame/icons.js'
import { NewSplitButton } from '../requests/new-split-button.js'

export function Properties({ me, onNew, onNewRequest, reloadSignal }: {
  me: MeResponse
  onNew?: () => void
  /**
   * New request beside the page's own New (`new-request.md` §13.1), absent
   * for somebody who can't take one — then the plain button stays.
   */
  onNewRequest?: () => void
  /** Bumped after New request creates something, so the list shows it (§11.4). */
  reloadSignal?: number
}) {
  /*
   * What every row carries whatever the view's columns are.
   *
   * `zoneColour` tints the map square on the mark; `address` is what the ⋯
   * menu copies and what the desktop's second line reads; `propertyType`,
   * `zoneName` and `orgUnitName` are the three parts of `cardLine` below. A
   * card cannot fall back to a column the list did not ask the server for —
   * the rows come back with the view's fields and nothing else — so a part
   * taken from a hidden column renders as a gap, which is what the phone's
   * second line was.
   */
  const list = useList('properties', {
    alwaysFetch: [
      'zoneName', 'zoneColour', 'address', 'propertyType', 'orgUnitName', 'city',
      'lastActivityAt',
    ],
  })
  /* The list is the page's, so the page reloads it — the engine is not changed (§11.4). */
  useEffect(() => { if (reloadSignal) list.reload() }, [reloadSignal])
  const navigate = useNavigate()

  const canEdit = me.permissions.properties === 'edit' || me.permissions.properties === 'full'

  return (
    <ListScreen
      area="properties"
      title="Properties"
      list={list}
      bulk={canEdit ? { actions: PROPERTY_BULK(list) } : undefined}
      actions={onNew || onNewRequest
        ? (
          <NewSplitButton label="New property" icon={IconPin} sub="just the property — the full form"
            phoneSub="just the property" onNew={onNew} onNewRequest={onNewRequest} />
        )
        : undefined}
      primaryColumn={{
        field: 'name',
        header: 'Property',
        /*
         * `address` is the composed one the area already builds — street,
         * city, state, postcode. It used to be `addressLine1`, which is not a
         * field of this area at all, so the second line under every property
         * name was the city or nothing (v3 §8).
         */
        render: (row: ListRow) => ({
          primary: String(row.name ?? ''),
          secondary: String(row.address ?? '') || undefined,
        }),
      }}
      cardLine={propertyCardLine}
      // A map square in the zone's own colour, so a list sorted by zone reads
      // as bands (v2 principle 1).
      mark={{ render: propertyMark }}
      tray={(row) => (
        <RowTray
          area="properties"
          id={row.id}
          actions={[
            { key: 'open', label: 'Open property', primary: true,
              onSelect: () => navigate(`/properties/${row.id}`) },
            { key: 'schedule', label: 'See the schedule',
              onSelect: () => navigate('/schedule') },
          ]}
        />
      )}
      rowMenu={(row) => [
        ...openItems(`/properties/${row.id}`, navigate),
        ...copyItem('copy-address', 'Copy address', row.address, true),
      ]}
      onRowClick={(row) => navigate(`/properties/${row.id}`)}
      emptyTitle="No properties yet"
      emptyBody="A property is somewhere you do the work. Add one, or import the addresses you already have."
      emptyAction={onNew ? { label: 'New property', onClick: onNew } : undefined}
      importOrgUnitId={me.scope.selected[0]}
    />
  )
}

/**
 * What the Properties list can do to a selection (v3 §3.3).
 *
 * Zone first, because it is the one anybody does to forty properties at
 * once: zones are how a crew's day is routed, and re-zoning a street one
 * record at a time is forty page loads.
 *
 * No Deactivate: `properties` has no active flag, so the engine adds none
 * (`PROPERTIES_AREA` says why). "Move to branch" and "Copy n addresses" from
 * the mockup are not in yet — the first needs the branch picker from the
 * scope tree rather than a flat list, and the second has the same problem as
 * Copy emails on Contacts: on a `matching` selection it would copy the fifty
 * rows that are loaded out of four hundred.
 */
const PROPERTY_BULK = (list: UseListResult): BulkAction[] => [
  {
    key: 'set_zone',
    group: 'change',
    label: () => 'Set zone\u2026',
    icon: IconPin,
    picker: (_ctx, done) => (
      <BulkRecordPicker
        list={list}
        field="zone"
        label="Which zone?"
        clearLabel="No zone"
        onPick={(zoneId) => done({ zoneId })}
      />
    ),
  },
  {
    key: 'set_managed_by',
    group: 'change',
    label: () => 'Set managed-by company\u2026',
    icon: IconCompany,
    picker: (_ctx, done) => (
      <BulkRecordPicker
        list={list}
        field="account"
        label="Which company?"
        clearLabel="Nobody"
        onPick={(accountId) => done({ accountId })}
      />
    ),
  },
]

/**
 * A property's third row: **Commercial · ● North · Harrisburg — Sep 26**.
 *
 * What it is, which zone it is in, which branch it belongs to, and when it
 * last did anything (v3 §8). The dot is the zone's own colour, so a list
 * scrolled past reads as bands the way the mark column does.
 *
 * A property has no status, so there is no chip — the engine's row starts at
 * the facts and the date still lands hard right, which is the whole point of
 * returning parts rather than markup.
 *
 * **A part that is empty drops out with its separator**: a property with no
 * zone reads "Commercial · Harrisburg", not "Commercial ·  · Harrisburg".
 */
function propertyCardLine(row: ListRow): CardLine {
  const zone = text(row.zoneName)
  const colour = text(row.zoneColour)
  return {
    facts: [
      text(row.propertyType) ? humanValue(row.propertyType) : null,
      zone
        ? (
          <span key="zone" className="inline-flex items-center gap-1.5">
            <i
              aria-hidden
              className="h-2 w-2 shrink-0 rounded-full bg-line-strong"
              {...(colour ? { style: { background: colour } } : {})}
            />
            {zone}
          </span>
        )
        : null,
      text(row.orgUnitName) || null,
    ].filter((part) => part !== null && part !== ''),
    trailing: lastActivity(row),
  }
}
